/**
 * useTheme Hook
 * Shared theme state (light/dark). All instances stay in sync via a tiny
 * subscriber store, so toggling in one component updates every consumer.
 */

import { useSyncExternalStore, useCallback } from 'react';
import { authService } from '@modules/auth';

type Theme = 'light' | 'dark';

interface UseThemeReturn {
  theme: Theme;
  isDark: boolean;
  isLight: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const listeners = new Set<() => void>();
let currentTheme: Theme = (() => {
  const initial = authService.getTheme();
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', initial);
  }
  return initial;
})();

function applyTheme(next: Theme): void {
  if (next === currentTheme) return;
  currentTheme = next;
  authService.setTheme(next);
  document.documentElement.setAttribute('data-theme', next);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): Theme {
  return currentTheme;
}

export function useTheme(): UseThemeReturn {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const toggleTheme = useCallback(() => {
    applyTheme(currentTheme === 'dark' ? 'light' : 'dark');
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    applyTheme(newTheme);
  }, []);

  return {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme,
  };
}
